// ============================================================================
// INFO TOOLTIP COMPONENT
//
// Tiny (i) icon that shows a tooltip on hover with:
// - 1-sentence definition of the metric
// - Formula used to compute it
//
// Usage:
//   <InfoTooltip
//     label="Completion Rate"
//     definition="Percentage of referrals that reached a usable outcome (routine or urgent)."
//     formula="(routine + urgent) / total runs"
//   />
// ============================================================================

import React, { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';

const InfoTooltip = ({ label, definition, formula }) => {
  const [open, setOpen] = useState(false);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        tooltipRef.current && !tooltipRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <span className="relative inline-flex items-center ml-1">
      <button
        ref={triggerRef}
        type="button"
        className="text-slate-500 hover:text-slate-300 transition-colors focus:outline-none focus:ring-1 focus:ring-slate-500 rounded-full"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen(prev => !prev)}
        aria-label={`Info: ${label}`}
        aria-describedby={open ? `tooltip-${label}` : undefined}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          ref={tooltipRef}
          id={`tooltip-${label}`}
          role="tooltip"
          className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-slate-900 border border-slate-700 rounded-lg shadow-xl p-3 text-left pointer-events-none"
        >
          <p className="text-xs text-slate-300 leading-relaxed mb-1">{definition}</p>
          {formula && (
            <p className="text-[10px] text-slate-500 font-mono mt-1">
              = {formula}
            </p>
          )}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 border-r border-b border-slate-700 rotate-45 -mt-1" />
        </div>
      )}
    </span>
  );
};

export default InfoTooltip;
